import {
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from '@mantine/core';
import { tokens } from './tokens';

/* Mantine theme for the Bit Haus console.
 *
 * The guiding rule, written up in ../../THEMING.md: reach for the cheapest lever
 * that lands the design. Theme tokens first, then component defaults, then the
 * Styles API, then a CSS module, and a bare <Box> only when a component would
 * have to be fought rather than configured.
 *
 * Two of those levers do most of the work here and are worth naming up front.
 *
 * 1. BRAND HUES ARE MAPPED OVER MANTINE'S STOCK HUE NAMES. Mantine's own
 *    internals paint stock colours we never asked for — a `color="red"` action,
 *    a gray track, a gray border. Remapping the stock names means those land on
 *    this palette instead of clashing with it, and it means `color="teal"` on a
 *    page reads as the design's teal.
 *
 * 2. THE `gray` RAMP IS PINNED TO THE DESIGN'S SURFACES. This is the single
 *    highest-leverage block in the file. SegmentedControl paints its track from
 *    gray.1, Progress its track from gray.2, inputs and tables their borders
 *    from gray.3, SegmentedControl its inactive label from gray.7. Getting the
 *    ramp right makes all four correct with no component override at all. */

const coral: MantineColorsTuple = [
  '#fdece8',
  tokens.coralSoft, // 1  #FBE3DC — light variant background
  '#f7c9bb',
  tokens.coralBorder, // 3  #F0A48E — light variant border
  '#f47f61',
  tokens.coral, // 5  #F2603F — the brand coral
  tokens.coralHover, // 6  #D74B2B — primaryShade: filled + hover base
  tokens.danger, // 7  #C4523A — destructive text
  tokens.coralDeep, // 8  #B04A2E — light variant foreground
  '#8f3a22',
];

const marigold: MantineColorsTuple = [
  '#fdf6e6',
  tokens.marigoldSoft2, // 1 #F0E2C6
  tokens.marigoldSoft, // 2 #F6E3B8
  '#f0cd7c',
  tokens.marigold, // 4 #E8B33C
  tokens.barWarn, // 5 #D9902E
  tokens.marigoldDeep, // 6 #C4622A
  '#a9611f',
  tokens.marigoldFg, // 8 #8A6A3A
  '#6b4f27',
];

const ok: MantineColorsTuple = [
  tokens.okCard, // 0 #F4F8F2
  tokens.okSoft, // 1 #DDEEE2
  '#bcdcc8',
  '#96c8a9',
  '#6fb389',
  '#4d9d6e',
  '#378c5c',
  tokens.okFg, // 7 #2C7A55
  '#236344',
  '#194a33',
];

const teal: MantineColorsTuple = [
  '#e7f7f4',
  tokens.tealSoft, // 1 #D6EFE8
  '#a9e0d5',
  '#74d0c1',
  '#43c0ae',
  '#1fb3a0',
  tokens.teal, // 6 #12A594 — filled must be exactly this
  tokens.tealFg, // 7 #17796B
  '#0f6459',
  '#084b43',
];

const periwinkle: MantineColorsTuple = [
  '#eef0fb',
  tokens.periwinkleSoft, // 1 #DFE2F6
  '#c2c8ec',
  '#a1aae0',
  '#818cd3',
  '#6772c2',
  tokens.periwinkle, // 6 #4C57A8
  '#3f4890',
  '#333a75',
  '#262c5b',
];

const violet: MantineColorsTuple = [
  '#f0ecfc',
  '#e2dbf9',
  '#c6b8f2',
  '#a894eb',
  '#8e75e0',
  tokens.violet, // 5 #7C5CD6
  '#6a49c4',
  '#573ba3',
  '#452e82',
  '#332262',
];

// The design's SECOND purple — the moonshot pill — is magenta-leaning and is
// not a shade of the chart violet above. Two purples, two names.
const moonshot: MantineColorsTuple = [
  '#f6ecf9',
  tokens.moonshotSoft, // 1 #EDDCF3
  '#dcc0e6',
  '#c9a2d8',
  '#b586c9',
  '#a26fba',
  '#8f5da8',
  tokens.moonshotFg, // 7 #7B4E96
  '#653d7b',
  '#4e2f5f',
];

// The warm stone ramp. See note 2 above — the indices are load-bearing.
const stone: MantineColorsTuple = [
  tokens.sidebar, // 0 #F7F1E7 — hover surface
  tokens.rail, // 1 #EFE8DC — SegmentedControl track
  tokens.page, // 2 #EDE4D5 — Progress track
  tokens.border, // 3 #E4DACB — input + table borders
  tokens.toggleOff, // 4 #D8CEBE — unchecked switch track
  tokens.faint, // 5 #C9BCA9 — disabled control text
  tokens.muted, // 6 #A79A88 — placeholder
  tokens.ink4, // 7 #8A7C6C — inactive segmented label
  tokens.ink3, // 8 #6B6157 — body copy
  tokens.ink2, // 9 #4A423A — table cell ink
];

export const theme = createTheme({
  fontFamily: tokens.fontUI,
  fontFamilyMonospace: tokens.fontMono,
  headings: {
    fontFamily: tokens.fontUI,
    fontWeight: '800',
    sizes: {
      h1: { fontSize: '34px', lineHeight: '1.15' },
      h2: { fontSize: '30px', lineHeight: '1.15' },
      h3: { fontSize: '18px', lineHeight: '1.3' },
      h4: { fontSize: '16px', lineHeight: '1.3' },
      h5: { fontSize: '14px', lineHeight: '1.3' },
      h6: { fontSize: '13px', lineHeight: '1.3' },
    },
  },
  primaryColor: 'coral',
  // Shade 6 is the semantic-contrast shade for every palette here. The brand's
  // lighter coral still paints primary SURFACES, through the resolver's
  // --mantine-primary-color-* overrides below.
  primaryShade: 6,
  defaultRadius: 'md',
  radius: { xs: '5px', sm: '8px', md: '10px', lg: '12px', xl: '14px' },
  colors: {
    coral,
    red: coral, // coral IS this palette's red
    orange: marigold,
    yellow: marigold,
    green: ok,
    teal,
    cyan: teal,
    blue: periwinkle, // the one genuinely cool hue in the design
    indigo: periwinkle,
    violet,
    grape: moonshot,
    gray: stone,
  },
  components: {
    Title: { styles: { root: { letterSpacing: '-0.02em' } } },
    Paper: {
      defaultProps: { bg: tokens.card, radius: 'xl', withBorder: true },
      styles: { root: { borderColor: tokens.cardBorder } },
    },
    // Actions are pills. Mantine's largest named radius is 14px, so the pill
    // has to be an explicit 999 rather than radius="xl".
    Button: {
      defaultProps: { radius: 999 },
      styles: { root: { fontWeight: 700 } },
    },
    ActionIcon: { defaultProps: { radius: 'sm' } },
    Anchor: { defaultProps: { underline: 'never' } },
    Progress: { defaultProps: { radius: 999, size: 7 } },
    Switch: { defaultProps: { color: 'coral', withThumbIndicator: false } },
    Tabs: {
      defaultProps: { color: 'coral' },
      styles: {
        list: { gap: 6 },
        tab: { fontSize: 14, fontWeight: 700, padding: '10px 16px' },
      },
    },
    Tooltip: { defaultProps: { radius: 'sm', withArrow: true } },
    TextInput: { defaultProps: { radius: 9 } },
    Textarea: { defaultProps: { radius: 9 } },
    PasswordInput: { defaultProps: { radius: 9 } },
    Select: { defaultProps: { radius: 9 } },
    NumberInput: { defaultProps: { radius: 9 } },
  },
});

/* Surfaces that Mantine owns but the design repaints.
 *
 * Emitted into BOTH `variables` and `light`. Mantine's own resolver defines the
 * scheme-dependent variables inside its light/dark sections, and those
 * out-cascade the root block — so a root-only override silently never lands.
 * This is the single most common way a Mantine rebrand fails. */
const surfaceVars: Record<string, string> = {
  '--mantine-color-body': tokens.page,
  '--mantine-color-text': tokens.ink,
  '--mantine-color-dimmed': tokens.ink4,
  '--mantine-color-placeholder': tokens.muted,
  '--mantine-color-default': tokens.card,
  '--mantine-color-default-hover': tokens.sidebar,
  '--mantine-color-default-color': tokens.ink,
  '--mantine-color-default-border': tokens.border,
  '--mantine-color-anchor': tokens.coral,

  '--mantine-primary-color-filled': tokens.coral,
  '--mantine-primary-color-filled-hover': tokens.coralHover,
  '--mantine-primary-color-light': tokens.coralSoft,
  '--mantine-primary-color-light-hover': '#f7cfc3',
  '--mantine-primary-color-light-color': tokens.coralDeep,
  '--mantine-primary-color-contrast': '#ffffff',

  // App-owned tokens, for the CSS module and the primitives. Named apart from
  // Mantine's so a future Mantine upgrade cannot collide with them.
  '--rt-card': tokens.card,
  '--rt-card-border': tokens.cardBorder,
  '--rt-sidebar': tokens.sidebar,
  '--rt-rail': tokens.rail,
  '--rt-page': tokens.page,
  '--rt-nav-active': tokens.navActive,
  '--rt-row-line': tokens.rowLine,
  '--rt-line-soft': tokens.lineSoft,
  '--rt-line-hard': tokens.lineHard,
  '--rt-row-hover': tokens.sidebar,
  '--rt-toggle-off': tokens.toggleOff,
  '--rt-ink': tokens.ink,
  '--rt-ink2': tokens.ink2,
  '--rt-ink3': tokens.ink3,
  '--rt-ink4': tokens.ink4,
  '--rt-ink5': tokens.ink5,
  '--rt-faint': tokens.faint,
  '--rt-coral': tokens.coral,
  '--rt-danger': tokens.danger,
};

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: { ...surfaceVars },
  light: { ...surfaceVars },
  dark: {},
});
